import React, { FC, useEffect, useCallback } from 'react';
import { SearchServiceQuery } from '../../../services/query';
import {
    Box,
    Button,
    Chip,
    Divider,
    Fade,
    IconButton,
    LinearProgress,
    Paper,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import TransgenderIcon from '@mui/icons-material/Transgender';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NumbersIcon from '@mui/icons-material/Numbers';
import PaletteIcon from '@mui/icons-material/Palette';
import StyleIcon from '@mui/icons-material/Style';
import { useTranslation } from 'react-i18next';

const BoolChip: FC<{ value: boolean }> = ({ value }) => (
    <Chip
        size="small"
        icon={
            value ? (
                <CheckCircleRoundedIcon sx={{ fontSize: '13px !important' }} />
            ) : (
                <CancelRoundedIcon sx={{ fontSize: '13px !important' }} />
            )
        }
        label={value ? 'true' : 'false'}
        color={value ? 'success' : 'error'}
        sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }}
    />
);

const GenderChip: FC<{ label: string }> = ({ label }) => {
    const lower = (label || '').toLowerCase();
    let icon: React.ReactElement = <TransgenderIcon sx={{ fontSize: '14px !important' }} />;
    let baseColor = '#6a1b9a';

    if (
        lower.includes('kadın') ||
        lower.includes('kadin') ||
        lower.includes('bayan') ||
        lower.includes('women') ||
        lower.includes('female')
    ) {
        icon = <FemaleIcon sx={{ fontSize: '14px !important' }} />;
        baseColor = '#c2185b';
    } else if (lower.includes('erkek') || lower.includes('men') || lower.includes('male')) {
        icon = <MaleIcon sx={{ fontSize: '14px !important' }} />;
        baseColor = '#1565c0';
    } else if (lower.includes('çocuk') || lower.includes('cocuk') || lower.includes('kids')) {
        baseColor = '#e65100';
    }

    return (
        <Chip
            size="small"
            icon={icon}
            label={label}
            variant="outlined"
            sx={{
                bgcolor: (theme) => alpha(baseColor, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: (theme) =>
                    theme.palette.mode === 'dark' ? theme.palette.getContrastText(alpha(baseColor, 0.38)) : baseColor,
                borderColor: (theme) => alpha(baseColor, theme.palette.mode === 'dark' ? 0.45 : 0.32),
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                border: '1.5px solid',
                '& .MuiChip-icon': { color: 'inherit' },
            }}
        />
    );
};

const MonoChip: FC<{ value: string | number; color?: string }> = ({ value, color = '#3730a3' }) => (
    <Chip
        size="small"
        label={value}
        sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.12),
            color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.getContrastText(alpha(color, 0.28)) : color),
            border: (theme) => `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.35 : 0.3)}`,
            fontSize: '0.75rem',
            height: 22,
            letterSpacing: '0.02em',
        }}
    />
);

const InfoRow: FC<{ label: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
    label,
    icon,
    children,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.1,
            borderBottom: '1px solid',
            borderColor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),
            transition: 'background-color 0.15s ease',
            px: 0.5,
            borderRadius: 0.5,
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05) },
            '&:last-child': { borderBottom: 'none' },
        }}
    >
        <Typography
            sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.77rem',
                width: '48%',
                flexShrink: 0,
                pr: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
            }}
        >
            {icon}
            {label}
        </Typography>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
);

export const SCUIProductSearchService: FC<{ id: number; onClose?: any }> = ({ id, onClose }) => {
    const [loading, setLoading] = React.useState(true);
    const [searchData, setSearchData] = React.useState<any>();
    const [searchProductData, setSearchProductData] = React.useState<any>();
    const { t } = useTranslation();
    const [adres, setAdres] = React.useState('');

    const GetStock = async (id: number) => {
        if (!id) {
            setLoading(false);
            return;
        }

        try {
            const res = await SearchServiceQuery.GetProductDetail(id);
            if (res?.data) {
                setSearchData(res.data);
                setSearchProductData(res.data.options);
            }
        } catch (error) {
            console.error('Error fetching product detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const GetTeknikFoy = useCallback(async () => {
        if (!searchData?.ozelKod1) {
            return;
        }

        const params = {
            OzelKod: searchData.ozelKod1,
        };

        try {
            const res = await SearchServiceQuery.GetTeknikFoy(params);
            setAdres(res?.data[0]?.imageUrl);
        } catch (error) {
            console.error('Error fetching teknik foy:', error);
            setAdres('');
        }
    }, [searchData?.ozelKod1, searchProductData]);

    useEffect(() => {
        GetStock(id);
    }, [id]);

    useEffect(() => {
        if (searchData?.ozelKod1 && searchProductData?.[0]?.colorCode) {
            GetTeknikFoy();
        }
    }, [searchData?.ozelKod1, searchProductData?.[0]?.colorCode, GetTeknikFoy]);

    if (!t) {
        return <div>Loading...</div>;
    }

    const getCultureValue = (value: string) => {
        const languageCode = JSON.parse(localStorage.getItem('languageObject') || '{}')?.languageCode || 'tr-TR';
        return searchData?.[value]?.[languageCode] || 'tr-TR';
    };

    const imageUrl = searchData?.picture;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
            {/* Loading bar */}
            <Fade in={loading} unmountOnExit>
                <LinearProgress
                    sx={{
                        '& .MuiLinearProgress-bar': {
                            background: (theme) =>
                                `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        },
                    }}
                />
            </Fade>

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.6)} 0%, ${alpha(
                                  theme.palette.primary.main,
                                  0.12
                              )} 100%)`
                            : 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                            border: (theme) =>
                                `1.5px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.15)}`
                                    : '0 2px 8px rgba(99,102,241,0.15)',
                        }}
                    >
                        <CategoryIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', lineHeight: 1.25 }}
                        >
                            {t('general.productInformation')}
                        </Typography>
                        {searchData?.brand && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                {searchData.brand}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                            borderColor: (theme) =>
                                alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
                            color: 'error.main',
                            transform: 'rotate(90deg)',
                            boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.error.main, 0.22)}`,
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                {searchData ? (
                    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                        {/* Left: image + actions */}
                        <Box sx={{ width: { xs: '100%', lg: 200 }, flexShrink: 0 }}>
                            {imageUrl && (
                                <Tooltip
                                    title={
                                        <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 1.5 }}>
                                            <img
                                                src={imageUrl}
                                                alt="product"
                                                style={{
                                                    maxWidth: 360,
                                                    maxHeight: 480,
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                            />
                                        </Box>
                                    }
                                    placement="right"
                                    arrow
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: 'background.paper',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                                borderRadius: 2.5,
                                                p: 0.5,
                                                maxWidth: 'none',
                                            },
                                        },
                                        arrow: { sx: { color: 'background.paper' } },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt="Product"
                                        loading="lazy"
                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                            e.currentTarget.style.display = 'none';
                                            console.error('Product image failed to load:', imageUrl);
                                        }}
                                        sx={{
                                            width: '100%',
                                            maxHeight: 260,
                                            objectFit: 'contain',
                                            borderRadius: 2.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                            cursor: 'zoom-in',
                                            display: 'block',
                                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                                            '&:hover': {
                                                transform: 'scale(1.03)',
                                                boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
                                            },
                                        }}
                                    />
                                </Tooltip>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                                    fullWidth
                                    onClick={() =>
                                        window.open(
                                            'https://www.lcw.com/model/?modelId=' +
                                                searchData?.modelId +
                                                '&optionId=' +
                                                id,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                    sx={{
                                        borderColor: (theme) =>
                                            alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.6 : 0.7),
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        fontSize: '0.78rem',
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: (theme) =>
                                                alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                                            borderColor: 'primary.main',
                                            boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    {t('general.goToProduct')}
                                </Button>

                                {adres ? (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ArticleIcon sx={{ fontSize: '15px !important' }} />}
                                        fullWidth
                                        onClick={() => window.open(adres, '_blank')}
                                        sx={{
                                            borderColor: (theme) =>
                                                alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.6 : 0.7),
                                            color: 'success.main',
                                            fontWeight: 600,
                                            fontSize: '0.78rem',
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: (theme) =>
                                                    alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                                                borderColor: 'success.main',
                                                boxShadow: (theme) => `0 2px 10px ${alpha(theme.palette.success.main, 0.25)}`,
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        {t('general.teknikFoy')}
                                    </Button>
                                ) : (
                                    <Chip
                                        size="small"
                                        icon={<CancelRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                        label={t('general.teknikFoyText')}
                                        color="error"
                                        variant="outlined"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 28 }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />

                        {/* Right: info list */}
                        <Paper
                            elevation={0}
                            sx={{
                                flex: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                minWidth: 260,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    background: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.text.primary, 0.04)
                                            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <StyleIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.07em',
                                        fontSize: '0.62rem',
                                        color: 'text.disabled',
                                    }}
                                >
                                    {t('general.productInformation')}
                                </Typography>
                            </Box>

                            <Box sx={{ px: 2, py: 0.5 }}>
                                <InfoRow
                                    label={t('searchService.modelTitle')}
                                    icon={<StyleIcon sx={{ fontSize: 12 }} />}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                        {getCultureValue('modelTitle')}
                                    </Typography>
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.brand')}
                                    icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                        {searchData?.brand}
                                    </Typography>
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.gender')}
                                    icon={<TransgenderIcon sx={{ fontSize: 12 }} />}
                                >
                                    <GenderChip label={getCultureValue('gender')} />
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.modelId')}
                                    icon={<NumbersIcon sx={{ fontSize: 12 }} />}
                                >
                                    <MonoChip value={searchData?.modelId} />
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.productGroup')}
                                    icon={<CategoryIcon sx={{ fontSize: 12 }} />}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                        {searchData?.productGroup}
                                    </Typography>
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.categoryName')}
                                    icon={<CategoryIcon sx={{ fontSize: 12 }} />}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                        {getCultureValue('categoryName')}
                                    </Typography>
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.specialCode')}
                                    icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                                >
                                    <MonoChip value={searchData?.ozelKod1} />
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.colorCode')}
                                    icon={<PaletteIcon sx={{ fontSize: 12 }} />}
                                >
                                    <MonoChip value={searchProductData?.[0]?.colorCode || 'N/A'} color="#7c3aed" />
                                </InfoRow>

                                <InfoRow
                                    label={t('searchService.optionId')}
                                    icon={<NumbersIcon sx={{ fontSize: 12 }} />}
                                >
                                    <MonoChip value={id} />
                                </InfoRow>

                                <InfoRow label={t('searchService.optionStatus')}>
                                    <BoolChip value={!!searchProductData?.[0]?.status} />
                                </InfoRow>

                                <InfoRow label={t('searchService.isPurchasable')}>
                                    <BoolChip value={!!searchData?.isPurchasable} />
                                </InfoRow>

                                <InfoRow label={t('searchService.isReturnable')}>
                                    <BoolChip value={!!searchData?.isReturnable} />
                                </InfoRow>

                                <InfoRow label={t('searchService.outlet')}>
                                    <BoolChip value={!!searchProductData?.[0]?.isOutlet} />
                                </InfoRow>
                            </Box>
                        </Paper>
                    </Box>
                ) : (
                    !loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '50%',
                                    bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.22),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SearchOffIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '1rem' }}
                            >
                                {t('general.notFound')}
                            </Typography>
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};
